import { useState, useCallback } from 'react';
import { useDeleteSessionMutation } from '~/api/chatbot/Chatbot.api';

export interface DeleteSessionResult {
    success: boolean;
    error?: string;
}

export interface UseDeleteSessionReturn {
    deleteSession: (sessionId: string) => Promise<DeleteSessionResult>;
    isDeleting: boolean;
    deleteError: string | null;
}

const hasErrorFlag = (response: unknown): boolean => {
    if (response && typeof response === 'object') {
        const data = response as Record<string, unknown>;
        return data.error === true || typeof data.error === 'string';
    }
    return false;
};

const extractErrorMessage = (response: unknown): string => {
    if (response && typeof response === 'object') {
        const data = response as Record<string, unknown>;
        if (typeof data.errorMessage === 'string') {
            return data.errorMessage;
        }
        if (typeof data.message === 'string') {
            return data.message;
        }
        if (typeof data.error === 'string') {
            return data.error;
        }
    }
    return 'Failed to delete session. Please try again.';
};

const isExplicitApiError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const err = error as Record<string, unknown>;

    if (err.status === 'FETCH_ERROR') {
        return false;
    }

    if (err.name === 'AbortError') {
        return false;
    }

    if (typeof err.error === 'string' && err.error.toLowerCase().includes('timeout')) {
        return false;
    }

    if (typeof err.status === 'number') {
        return true;
    }

    if (err.data && typeof err.data === 'object') {
        return true;
    }

    return false;
};

export const useDeleteSession = (): UseDeleteSessionReturn => {
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [deleteSessionMutation] = useDeleteSessionMutation();

    const deleteSession = useCallback(
        async (sessionId: string): Promise<DeleteSessionResult> => {
            setIsDeleting(true);
            setDeleteError(null);

            try {
                const response = await deleteSessionMutation(sessionId).unwrap();

                if (hasErrorFlag(response)) {
                    const errorMessage = extractErrorMessage(response);
                    setDeleteError(errorMessage);
                    return { success: false, error: errorMessage };
                }

                return { success: true };
            } catch (err: unknown) {
                if (isExplicitApiError(err)) {
                    const errorMessage = extractErrorMessage(
                        (err as Record<string, unknown>).data ?? err
                    );
                    setDeleteError(errorMessage);
                    return { success: false, error: errorMessage };
                }

                return { success: false };
            } finally {
                setIsDeleting(false);
            }
        },
        [deleteSessionMutation]
    );

    return {
        deleteSession,
        isDeleting,
        deleteError,
    };
};
